import { Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import AssetTypesLinksList from "@/components/AssetType/AssetTypesLinksList";
import Revision from "@/components/InterventionTemplate/Revision";
import { TemplateRevisions } from "@/components/InterventionTemplate/TemplateRevisions";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { FlexContainerText, FlexContainerJsx } from "@/modules/flex-container";
import InterventionsListBreadcrumb from "@/modules/interventions/interventions-list-breadcrumb";
import LangName from "@/modules/language/language-name";
import { useModelLink } from "@/modules/model-link";
import ModulesTagList from "@/modules/modules/modules-tag-list";

const { Text } = Typography;

const Info = ({ template }) => {
  const { t } = useTranslation();
  const { types } = useInterventionType();
  const { getModelLink } = useModelLink();

  const {
    name,
    lang,
    description,
    intervention_type_id,
    state,
    version,
    reference,
    interventions,
    organizations,
    asset_types,
    modules,
  } = template;

  const templateRevisions = new TemplateRevisions(template);
  const revs = templateRevisions
    .getRevisions()
    .filter((rev) => rev.id != template.id);

  return (
    <Space orientation="vertical" size="middle">
      {name && (
        <FlexContainerText
          value={name}
          label={t("interventionTemplate:form.name.label")}
        />
      )}
      {lang && (
        <FlexContainerJsx
          value={<LangName code={lang} />}
          label={t("global:language.label")}
        />
      )}
      {description && (
        <FlexContainerText
          value={description}
          label={t("interventionTemplate:form.description.label")}
        />
      )}
      {intervention_type_id && (
        <FlexContainerText
          value={types.find(({ id }) => id === intervention_type_id)?.name}
          label={t("interventionTemplate:form.type.label")}
        />
      )}
      {organizations?.length > 0 && (
        <FlexContainerJsx
          label={t("interventionTemplate:form.organizations.label")}
          value={
            <Text size="sm">
              {organizations.map((org) => org.name).join(", ")}
            </Text>
          }
        />
      )}
      {asset_types?.length > 0 && (
        <FlexContainerJsx
          label={t("interventionTemplate:form.assetTypes.label")}
          value={<AssetTypesLinksList assetTypes={asset_types} />}
        />
      )}
      {modules?.length > 0 && (
        <FlexContainerJsx
          label={t("interventionTemplate:form.modules.label")}
          value={<ModulesTagList modules={modules} />}
        />
      )}
      {state && (
        <Revision
          label={t("interventionTemplate:version.currentVersion")}
          version={version}
          state={state}
          stateType="interventiontemplate"
          additionalText={
            template.id && templateRevisions.isLatest(template.id)
              ? t("interventionTemplate:version.latest")
              : ""
          }
        />
      )}
      {reference && reference.state && (
        <Revision
          label={t("interventionTemplate:version.revisionOf")}
          name={reference.name}
          version={reference.version}
          state={reference.state}
          stateType="interventiontemplate"
          link={getModelLink("interventiontemplate", reference)}
        />
      )}
      {revs.length > 0 && (
        <div>
          <Text strong>{t("interventionTemplate:version.otherRevisions")}</Text>
          {revs.map((filtredRevision) => (
            <div key={filtredRevision.id} style={{ listStyleType: "none" }}>
              <Revision
                name={filtredRevision.name}
                version={filtredRevision.version}
                state={filtredRevision.state}
                stateType="interventiontemplate"
                additionalText={
                  templateRevisions.isLatest(filtredRevision.id)
                    ? t("interventionTemplate:version.latest")
                    : ""
                }
                link={getModelLink("interventiontemplate", filtredRevision)}
              />
            </div>
          ))}
        </div>
      )}

      {interventions && interventions.length > 0 && (
        <FlexContainerJsx
          value={<InterventionsListBreadcrumb interventions={interventions} />}
          label={t("interventionTemplate:form.usedInIntervention.label")}
        />
      )}
    </Space>
  );
};

export default Info;
